import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Drawer,
  DataTable,
  EmptyState,
  Field,
  ModulePage,
  Resourceful,
  Select,
  StatusBadge,
  TextInput,
  Toolbar,
  type BadgeTone,
  type Column,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import { allows, announceSitesChanged, sites as loadSites, type SiteSummary } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  createSite,
  getSite,
  patchSite,
  serviceFormats as loadFormats,
  TIMEZONES,
  WEEKDAYS,
  type Site,
  type SiteStatus,
  type WorkingHours,
  type Weekday,
} from './api';
import { makeSitesT, type SitesLocale, type SitesTranslate } from './i18n';

/**
 * Раздел «Объекты».
 *
 * ТЗ §8.5 требует, чтобы открытие новой мойки было конфигурацией, а не
 * доработкой: до этого раздела объект заводил только сид, то есть каждый новый
 * комплекс означал правку кода и выкладку.
 *
 * Гейт один — `admin.manage`, сетевое право. Управляющему объекта здесь делать
 * нечего: объект принадлежит сети, а не ему.
 */

const STATUSES: readonly SiteStatus[] = ['active', 'suspended', 'closed'];

function statusTone(status: SiteStatus): BadgeTone {
  if (status === 'active') return 'ok';
  if (status === 'suspended') return 'warn';
  return 'neutral';
}

interface HoursEditorProps {
  readonly t: SitesTranslate;
  readonly value: WorkingHours;
  readonly onChange: (next: WorkingHours) => void;
}

function HoursEditor({ t, value, onChange }: HoursEditorProps) {
  return (
    <div className="flex flex-col gap-2">
      {WEEKDAYS.map((day: Weekday) => {
        const entry = value[day];
        const off = entry === null;
        return (
          <div key={day} className="flex flex-wrap items-center gap-2">
            <span className="w-8 text-sm text-ink-muted">{t(`sites.day.${day}` as never)}</span>
            <TextInput
              type="time"
              aria-label={`${t(`sites.day.${day}` as never)} ${t('sites.form.open')}`}
              value={entry?.open ?? ''}
              disabled={off}
              onChange={(e) =>
                onChange({
                  ...value,
                  [day]: { open: e.target.value, close: entry?.close ?? '' },
                })
              }
            />
            <span className="text-sm text-ink-muted">{t('sites.form.close')}</span>
            <TextInput
              type="time"
              aria-label={`${t(`sites.day.${day}` as never)} ${t('sites.form.close')}`}
              value={entry?.close ?? ''}
              disabled={off}
              onChange={(e) =>
                onChange({
                  ...value,
                  [day]: { open: entry?.open ?? '', close: e.target.value },
                })
              }
            />
            <label className="flex items-center gap-1 text-sm text-ink-muted">
              <input
                type="checkbox"
                checked={off}
                onChange={(e) => onChange({ ...value, [day]: e.target.checked ? null : undefined })}
              />
              {t('sites.form.dayOff')}
            </label>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Отбрасываем незаполненные дни: сервер принимает либо пару «открытие +
 * закрытие», либо `null` (выходной). Полупустой день — это «не задано», и его
 * отправка стоила бы отказа по валидации на ровном месте.
 */
function cleanHours(hours: WorkingHours): WorkingHours {
  const out: WorkingHours = {};
  for (const day of WEEKDAYS) {
    const entry = hours[day];
    if (entry === null) out[day] = null;
    else if (entry && entry.open && entry.close) out[day] = entry;
  }
  return out;
}

export default function SitesModule({ user, title }: ModuleViewProps) {
  const locale: SitesLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeSitesT(locale), [locale]);
  const toast = useToast();

  const canManage = allows(user, 'admin.manage');
  const [reloadKey, setReloadKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState('');

  const list = useResource(() => loadSites(), [reloadKey], { enabled: canManage });

  if (!canManage) {
    return (
      <ModulePage title={title} subtitle={t('sites.subtitle')}>
        <EmptyState title={t('sites.forbidden')} reason={t('sites.forbiddenReason')} />
      </ModulePage>
    );
  }

  const columns: ReadonlyArray<Column<SiteSummary>> = [
    { key: 'code', header: t('sites.col.code'), render: (row) => row.code, width: '8rem' },
    { key: 'name', header: t('sites.col.name'), render: (row) => row.name },
    {
      key: 'status',
      header: t('sites.col.status'),
      width: '10rem',
      render: (row) => (
        <StatusBadge tone={statusTone(row.status as SiteStatus)}>
          {t(`sites.status.${row.status}` as never)}
        </StatusBadge>
      ),
    },
  ];

  return (
    <ModulePage title={title} subtitle={t('sites.subtitle')}>
      <Toolbar>
        <Button tone="primary" onClick={() => setCreating(true)}>
          {t('sites.add')}
        </Button>
      </Toolbar>

      {creating ? (
        <SiteForm
          t={t}
          locale={locale}
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={(site) => {
            setCreating(false);
            setReloadKey((v) => v + 1);
            announceSitesChanged();
            toast.ok(t('sites.created'), site.code);
          }}
        />
      ) : null}

      {openId ? (
        <SiteForm
          t={t}
          locale={locale}
          mode="edit"
          siteId={openId}
          onClose={() => setOpenId('')}
          onSaved={() => {
            setOpenId('');
            setReloadKey((v) => v + 1);
            announceSitesChanged();
            toast.ok(t('sites.saved'));
          }}
        />
      ) : null}

      <Resourceful resource={list}>
        {(rows) => (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            onRowClick={(row) => setOpenId(row.id)}
            activeKey={openId}
            empty={t('sites.empty')}
            emptyReason={t('sites.emptyReason')}
            loading={list.loading}
          />
        )}
      </Resourceful>
    </ModulePage>
  );
}

interface SiteFormProps {
  readonly t: SitesTranslate;
  readonly locale: SitesLocale;
  readonly mode: 'create' | 'edit';
  readonly siteId?: string;
  readonly onClose: () => void;
  readonly onSaved: (site: Site) => void;
}

function SiteForm({ t, locale, mode, siteId, onClose, onSaved }: SiteFormProps) {
  const toast = useToast();
  const formatsRes = useResource(() => loadFormats(), []);
  const [formats, setFormats] = useState<readonly string[]>([]);
  const existing = useResource(() => (siteId ? getSite(siteId) : Promise.resolve(undefined)), [
    siteId,
  ]);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [timezone, setTimezone] = useState<string>(TIMEZONES[0]);
  const [status, setStatus] = useState<SiteStatus>('active');
  const [hours, setHours] = useState<WorkingHours>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  // Форма заполняется один раз, когда карточка приехала: переливать состояние
  // на каждый рендер — значит затирать то, что человек уже успел напечатать.
  if (mode === 'edit' && !loaded && existing.data) {
    const site = existing.data;
    setCode(site.code);
    setName(site.name);
    setAddress(site.address);
    setTimezone(site.timezone);
    setStatus(site.status);
    setHours(site.workingHours ?? {});
    setFormats(site.serviceFormats ?? []);
    setLoaded(true);
  }

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const cleaned = cleanHours(hours);
      const saved =
        mode === 'create'
          ? await createSite({
              code,
              name,
              address,
              timezone,
              workingHours: cleaned,
              serviceFormats: formats,
            })
          : await patchSite(siteId ?? '', {
              name,
              address,
              timezone,
              status,
              workingHours: cleaned,
              serviceFormats: formats,
            });
      onSaved(saved);
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title={mode === 'create' ? t('sites.form.new') : name || t('sites.form.new')}
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Button
            tone="primary"
            onClick={submit}
            disabled={busy || !name || (mode === 'create' && !code)}
          >
            {mode === 'create' ? t('sites.form.create') : t('sites.form.save')}
          </Button>
          <Button onClick={onClose} disabled={busy}>
            {t('sites.form.cancel')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {mode === 'create' ? (
          <Field label={t('sites.form.code')} hint={t('sites.form.codeHint')}>
            <TextInput value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          </Field>
        ) : (
          /* Код объекта не меняется: он уже разошёлся по отчётам и бейджам. */
          <Field label={t('sites.form.code')}>
            <TextInput value={code} readOnly disabled />
          </Field>
        )}

        <Field label={t('sites.form.name')}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field label={t('sites.form.address')}>
          <TextInput value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>

        <Field label={t('sites.form.timezone')} hint={t('sites.form.timezoneHint')}>
          <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Field>

        {mode === 'edit' ? (
          <Field label={t('sites.form.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as SiteStatus)}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`sites.status.${s}` as never)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label={t('sites.form.formats')} hint={t('sites.form.formatsHint')}>
          <Resourceful resource={formatsRes}>
            {(list) => (
              <div className="grid gap-2 sm:grid-cols-2">
                {list.map((f) => (
                  <label key={f.code} className="flex items-start gap-2 text-sm text-ink" title={f.description}>
                    <Checkbox
                      checked={formats.includes(f.code)}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setFormats((prev) =>
                          on ? [...prev, f.code] : prev.filter((code) => code !== f.code),
                        );
                      }}
                    />
                    <span>{locale === 'kk' ? f.nameKk : f.nameRu}</span>
                  </label>
                ))}
              </div>
            )}
          </Resourceful>
        </Field>

        <Field label={t('sites.form.hours')} hint={t('sites.form.hoursHint')}>
          <HoursEditor t={t} value={hours} onChange={setHours} />
        </Field>
      </div>
    </Drawer>
  );
}
